// @flow

import React from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import classNames from 'classnames';

import useSheet from '../../jss';
import { IN_PROGRESS } from '../../constants/challengeStatusTypes';
import { isInProgress } from '../../utils/challengeUtils';
import MatchScore from './MatchScore';
import MatchTime from './MatchTime';

type Props = {
  awayScore?: string,
  awayTeam?: string,
  gameName: string,
  homeScore?: string,
  homeTeam?: string,
  matchType?: string,
  progress?: string,
  reverseOrder?: boolean,
  classes: Object,
  scheme: string,
  startTime?: string,
  statusId: number,
  venue?: string,
  lang: string
};

const defaultProps = {
  awayTeam: '',
  awayScore: '',
  homeTeam: '',
  homeScore: '',
  matchType: 'Match',
  progress: '',
  reverseOrder: false,
  startTime: '',
  venue: ''
};

const MatchCard = styled.div(
  {
    display: 'flex',
    flexBasis: '100%',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: '100%',
    position: 'relative'
  },
  ({ theme }) => ({
    padding: theme.spacing.small
  }),
  ({ theme, scheme }) =>
    ({
      white: {
        border: `1px solid ${theme.color.basis4}`
      },
      light: {
        backgroundColor: theme.color.basis2,
        borderRadius: theme.borderRadius.default,
        border: `1px solid ${theme.color.basis4}`,
        color: theme.color.white
      },
      dark: {
        backgroundColor: theme.color.basis2,
        borderRadius: theme.borderRadius.default,
        border: `1px solid ${theme.color.basis4}`
      }
    }[scheme]),
  ({ theme }) => ({
    [theme.mediaQuery.mobileOnly]: {
      border: 'none',
      borderRadius: 0,
      borderBottom: `1px solid ${theme.color.basis4}`
    }
  })
);

function Match({
  awayScore,
  awayTeam,
  classes,
  gameName,
  homeScore,
  homeTeam,
  matchType,
  lang,
  progress,
  reverseOrder,
  scheme,
  startTime,
  statusId,
  venue
}: Props) {
  const isNumberScore = !Number.isNaN(awayScore) && !Number.isNaN(homeScore);

  return (
    <MatchCard scheme={scheme}>
      <div className={classes.matchDetail}>
        <MatchScore
          awayScore={statusId >= IN_PROGRESS ? awayScore : undefined}
          awayTeam={awayTeam}
          gameName={gameName}
          homeScore={statusId >= IN_PROGRESS ? homeScore : undefined}
          homeTeam={homeTeam}
          reverseOrder={reverseOrder}
          isNumberScore={isNumberScore}
        />
      </div>
      <div
        className={classNames(classes.matchInfo, {
          [classes.indent]: statusId >= IN_PROGRESS && isNumberScore && awayScore !== homeScore
        })}
      >
        <div className={classNames(classes.matchProgress, { live: isInProgress(statusId) })}>
          <MatchTime
            matchType={matchType}
            lang={lang}
            progress={progress}
            startTime={startTime}
            statusId={statusId}
          />
        </div>
        <div className={classes.matchVenue}>{venue}</div>
      </div>
    </MatchCard>
  );
}

Match.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    matchDetail: {
      // color: theme.color.white,
      maxWidth: '100%' // IE11 hack
    },
    matchInfo: {
      color: theme.color.basis5,
      display: 'flex',
      flexDirection: 'column',
      fontSize: theme.fontSize.tiny,
      textTransform: 'uppercase'
    },
    matchProgress: {
      '&.live': {
        color: theme.color.primary
      }
    },
    matchPreview: {
      textTransform: 'uppercase'
    },
    matchVenue: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap'
    },
    indent: {
      paddingLeft: theme.spacing.small
    }
  };
}

function mapStateToProps(state) {
  return {
    lang: state.settings.language
  };
}

export default useSheet(applyStyles)(connect(mapStateToProps, {})(Match));
